import type { ClaimInterval } from "../../lib/claims.js";

/**
 * Every confidence on the wire must be a finite number.
 *
 * [V] This exists because it was not checked. `scoreSpan` takes the template centroid as a
 * bare array; the engine handed it `{ centroid }`, so `cosine` indexed an object, every
 * window scored `NaN`, and `NaN` walked the whole pipeline unchallenged: `NaN < floor` is
 * false so the claim floor let it through, `Math.floor(NaN / band)` banded it, and
 * `Math.round(NaN * 1e4) / 1e4` rounded it. `JSON.stringify` then serialises `NaN` as
 * `null`, and the server answered with one `float_type` validation error per interval --
 * on a bundle the outbox could never deliver, for a session with real speech in it.
 *
 * The type declaration that permitted the wrong call is fixed, so this cannot recur in that
 * exact form. It is here for the next one: a shape mismatch anywhere upstream of the wire
 * should fail at the developer, loudly, before a signed bundle is built -- not at the
 * server, twenty intervals at a time, on somebody's real conversation.
 *
 * Deliberately before `buildPayload`: a bundle is signed, and a signature over nonsense is
 * still a valid signature over nonsense.
 */
export function assertFiniteConfidences(intervals: ClaimInterval[]): void {
  for (const interval of intervals) {
    if (!Number.isFinite(interval.confidence)) {
      throw new Error(
        `scored a non-finite confidence (${interval.confidence}) at ${interval.start}-${interval.end}s; ` +
          "this is a bug in the scoring path, not a network problem"
      );
    }
    if (!Number.isFinite(interval.start) || !Number.isFinite(interval.end)) {
      throw new Error(
        `scored a non-finite interval bound (${interval.start}-${interval.end}); ` +
          "this is a bug in the scoring path, not a network problem"
      );
    }
  }
}

/** What the server's refusal of a claim bundle actually means for the queue. */
export type ClaimVerdict = "signed-out" | "terminal" | "rejected" | "retry";

/**
 * Classify a failed claim POST once, in one place.
 *
 * Four answers, and the queue does something different with each: tear this device down
 * (`signed-out`), clear the queue because the session is over (`terminal`), drop this
 * bundle and carry on (`rejected`), or leave it at the head and try again (`retry`).
 *
 * The line between the last two is *what* versus *when*. A bundle is signed, so its bytes
 * are fixed: 400, 403, 404, a plain 409 and 422 are all judgements about those bytes and
 * will be repeated forever. 408 and 429 are about timing -- and `api.js` already retries
 * 429 with `Retry-After` before it ever reaches here. 5xx is the server having a bad moment.
 * A network failure carries no status at all, which is the outage case and must stay a
 * retry, or an offline device would throw away every claim it made.
 *
 * Here rather than in `session.ts` so it can be asserted without a DOM: [V] the cost of
 * getting it wrong was a queue that stopped moving on a live session with real speech in it,
 * reported to its owner as "2 claims waiting to upload".
 */
export function classifyClaimError(err: Error & { status?: number }): ClaimVerdict {
  const status = err?.status;
  if (status === 401) return "signed-out";
  // The session moved on. Matched on the message as well as the code, because a 409 is also
  // how a duplicate nonce comes back, and that one is about a single bundle.
  if (status === 409 && /no longer accepts claims/.test(err.message || "")) return "terminal";
  if (typeof status !== "number" || status < 400 || status >= 500) return "retry";
  if (status === 408 || status === 429) return "retry";
  return "rejected";
}

/**
 * What the server's refusal of a *claim delta* means for the delta queue.
 *
 * Five answers where the legacy bundle has four, and the extra two are the whole
 * difference between a bundle queue and a ledger writer:
 *
 * - `uncertain` -- the request produced no answer this device can trust (a dropped
 *   connection, a 5xx, a 408). A bundle could simply be resent; a delta cannot, because
 *   the write may already have committed and the queue must not act on a guess. It is
 *   resolved by asking the server what it holds (`resume`), never by allocating a new
 *   sequence.
 * - `stale-context` -- the outer producer lease was fenced out between reading it and
 *   quoting it. Nothing is wrong with the *payload*; the envelope around it is out of
 *   date. Fixed by re-reading the lease and re-wrapping the same signed bytes.
 *
 * `permanent` is deliberately not the legacy `rejected`. A bundle queue drops a refused
 * bundle and carries on, because bundles are independent. Delta sequences are
 * contiguous: sequence N being refused forever means N+1 can never be accepted either,
 * so "drop it and continue" would silently stall the whole ledger while reporting
 * progress. A permanent refusal stops the writer and is surfaced, and it is never
 * converted back into a legacy claim -- the payload protocol does not change under a
 * live session.
 */
export type ClaimDeltaVerdict =
  | "signed-out"
  | "terminal"
  | "permanent"
  | "stale-context"
  | "uncertain"
  | "retry";

/** 409 reasons that mean "the lease is stale", not "the payload is wrong". */
const STALE_CONTEXT_REASONS = new Set([
  "producer_not_accepted",
  "producer_fence_conflict",
  "stale_fence",
  "future_fence",
  "stale_session_generation",
  "future_generation",
]);

/** The reason string the append and resume routes put in a structured 409/410 body. */
function conflictReason(err: { data?: unknown }): string | null {
  const detail = (err?.data as { detail?: unknown } | undefined)?.detail;
  if (!detail || typeof detail !== "object") return null;
  const reason = (detail as { error?: unknown }).error;
  return typeof reason === "string" ? reason : null;
}

/**
 * The server's reason for a permanent claim-delta refusal, for diagnostics only.
 *
 * A ledger refusal carries a structured `detail.error` (a stable code); the ingest
 * check answers with a plain-string `detail` (its human message). Either is more
 * useful than a constant, and neither is the signed payload. It falls back to the
 * transport message and finally `refused`.
 */
export function claimDeltaRefusalReason(err: Error & { data?: unknown }): string {
  const code = conflictReason(err);
  if (code) return code;
  const detail = (err?.data as { detail?: unknown } | undefined)?.detail;
  if (typeof detail === "string" && detail) return detail;
  return err?.message || "refused";
}

export function classifyClaimDeltaError(
  err: Error & { status?: number; data?: unknown }
): ClaimDeltaVerdict {
  const status = err?.status;
  if (status === 401) return "signed-out";

  // No status at all is a network failure -- which is the one case where the request
  // may have been received, executed and committed with only the answer lost. It is
  // never a plain retry for a ledger writer.
  if (typeof status !== "number") return "uncertain";

  if (status === 408 || status >= 500) return "uncertain";
  if (status === 429) return "retry";

  if (status === 409) {
    const reason = conflictReason(err);
    if (reason && STALE_CONTEXT_REASONS.has(reason)) return "stale-context";
    // The session moved on. Matched on the message as well, because the ledger reports
    // a closed session as a plain-string 409 detail rather than a structured reason.
    if (/no longer accepts claims/.test(err.message || "")) return "terminal";
    // Everything else at 409 -- a sequence gap, an idempotency conflict, a digest
    // mismatch -- is a statement about bytes that are already fixed. Waiting cannot
    // change it and neither can a new sequence.
    return "permanent";
  }

  if (status >= 400 && status < 500) return "permanent";
  return "uncertain";
}
